import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { toolboxTalksService } from '../../../services/toolboxTalksService';

const TalkCard = ({ assignment, onConfirmed }) => {
  const talk = assignment?.talk;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(assignment?.confirmed || false);
  const [confirmedAt, setConfirmedAt] = useState(assignment?.confirmed_at || null);
  const [viewLoading, setViewLoading] = useState(false);

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleViewNotes = async () => {
    setError('');
    // Defensive guard mirroring RamsCard from [gwwbt45U]. The View Notes
    // button is already conditionally rendered on `talk?.file_path` (see
    // below), but defending in the handler is consistent and protects
    // against future code paths that bypass the UI gate.
    if (!talk?.file_path) {
      console.error('[TalkCard] view notes: talk embed missing', { assignmentId: assignment?.id, talkId: assignment?.talk_id });
      setError("This talk's notes are unavailable. Please contact your site admin — the assignment may reference a deleted document.");
      return;
    }

    // Open the destination tab synchronously inside the click handler so
    // the user-gesture chain is preserved across the upcoming `await`.
    // Modern browsers (Safari especially) silently block `window.open` if
    // it runs after an async hop. Same fix as RamsCard from [gwwbt45U].
    const newWin = window?.open('', '_blank', 'noopener,noreferrer');

    setViewLoading(true);
    const result = await toolboxTalksService?.getSignedUrl(talk?.file_path, talk?.title);
    setViewLoading(false);

    if (result?.url) {
      if (newWin) {
        newWin.location.href = result.url;
      } else {
        // Even the synchronous open was blocked. Fall back to in-place
        // navigation — the signed URL is download-attachment, so the
        // browser handles it as a download without leaving the SPA.
        window.location.href = result.url;
      }
      return;
    }

    if (newWin) newWin.close();

    const err = result?.error;
    console.error('[TalkCard] view document failed:', { filePath: talk?.file_path, code: err?.code, message: err?.message });
    if (err?.code === 'PGRST301') {
      setError(`Could not open document — permission denied: ${err?.message}`);
    } else {
      setError(`Could not open document: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    const result = await toolboxTalksService?.confirmAttendance(talk?.id, assignment?.worker_user_id);
    setLoading(false);
    if (result?.error) {
      const err = result?.error;
      console.error('[TalkCard] confirm attendance failed:', { talkId: talk?.id, code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Failed to confirm attendance — permission denied: ${err?.message}`);
      } else {
        setError(`Failed to confirm attendance: ${err?.message || 'Unknown error'}`);
      }
      return;
    }
    const ts = result?.data?.confirmed_at || new Date()?.toISOString();
    setConfirmed(true);
    setConfirmedAt(ts);
    onConfirmed?.(talk?.id);
  };

  return (
    <div className={`bg-card border rounded-xl p-5 transition-all ${
      confirmed ? 'border-success/30' : 'border-amber-300'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            confirmed ? 'bg-success/10' : 'bg-amber-50'
          }`}>
            <Icon
              name={confirmed ? 'MessageSquareCheck' : 'MessageSquare'}
              size={20}
              className={confirmed ? 'text-success' : 'text-amber-600'}
            />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base leading-tight">{talk?.title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {talk?.site?.site_name && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="MapPin" size={11} />
                  {talk?.site?.site_name}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Calendar" size={11} />
                {formatDate(talk?.created_at)?.split(',')?.[0]}
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {confirmed ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success shrink-0">
            <Icon name="CheckCircle" size={12} />
            Confirmed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 shrink-0">
            <Icon name="Clock" size={12} />
            Pending
          </span>
        )}
      </div>

      {/* Confirmed timestamp */}
      {confirmed && confirmedAt && (
        <div className="flex items-center gap-2 bg-success/5 border border-success/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="CheckCircle" size={14} className="text-success shrink-0" />
          <p className="text-xs text-success font-medium">
            You confirmed attendance on {formatDate(confirmedAt)}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="AlertCircle" size={14} className="text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        {talk?.file_path && (
          <Button
            variant="outline"
            onClick={handleViewNotes}
            disabled={viewLoading}
            className="flex-1 h-10"
          >
            {viewLoading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-2" />Opening...</>
            ) : (
              <><Icon name="ExternalLink" size={15} className="mr-2" />View Notes</>
            )}
          </Button>
        )}

        {!confirmed && (
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className={`h-10 bg-success hover:bg-success/90 text-white ${
              talk?.file_path ? 'flex-1' : 'w-full'
            }`}
          >
            {loading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Confirming...</>
            ) : (
              <><Icon name="CheckCircle" size={15} className="mr-2" />Confirm Attendance</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TalkCard;
